import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

export interface ThemeColors {
  light: { primary: string; accent: string; [key: string]: string };
  dark: { primary: string; accent: string; [key: string]: string };
}

export interface ColorPreset {
  name: string;
  primary: string;
  accent: string;
}

export interface ThemeContextType {
  colors: ThemeColors;
  updateColor: (
    mode: "light" | "dark",
    key: string,
    value: string
  ) => Promise<void>;
  resetColors: () => Promise<void>;
  applyPreset: (preset: ColorPreset) => Promise<void>;
  saveColorsToDb: (colorValues: ThemeColors) => Promise<void>;
  loading: boolean;
  error: string | null;
}

export const defaultColors: ThemeColors = {
  light: { primary: "#2C3E50", accent: "#3D5A74" },
  dark: { primary: "#3D5A74", accent: "#4A6B8A" },
};

const ThemeContext = createContext<ThemeContextType>({
  colors: defaultColors,
  updateColor: async () => {},
  resetColors: async () => {},
  applyPreset: async () => {},
  saveColorsToDb: async () => {},
  loading: false,
  error: null,
});
export const useTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [colors, setColors] = useState<ThemeColors>(defaultColors);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchThemeColors();
  }, []);

  async function fetchThemeColors() {
    setLoading(true);
    setError(null);
    try {
      // 1) Load from localStorage immediately
      const cached = localStorage.getItem("theme-colors");
      if (cached) setColors(JSON.parse(cached));

      // 2) Then fetch from DB
      const res = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", "theme_colors")
        .maybeSingle();

      if (res.error) throw res.error;
      if (res.data) {
        // Safely cast through unknown to satisfy TS
        const dbColors = res.data.value as unknown as ThemeColors;
        setColors(dbColors);
        localStorage.setItem("theme-colors", JSON.stringify(dbColors));
      } else {
        // No row yet → initialize
        await saveColorsToDb(defaultColors);
      }
    } catch (err) {
      console.error("Error fetching theme colors:", err);
      setError("Failed to load theme colors");
    } finally {
      setLoading(false);
    }
  }

  async function saveColorsToDb(colorValues: ThemeColors) {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        localStorage.setItem("theme-colors", JSON.stringify(colorValues));
        console.log(
          "User not authenticated, saved colors to localStorage only"
        );
        return;
      }

      const { error: upsertError } = await supabase.from("app_settings").upsert(
        {
          key: "theme_colors",
          value: colorValues as unknown as Json,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "key" }
      );

      if (upsertError) throw upsertError;

      localStorage.setItem("theme-colors", JSON.stringify(colorValues));
    } catch (err) {
      console.error("Error saving theme colors:", err);
      throw new Error("Failed to save theme colors");
    }
  }

  const updateColor = async (
    mode: "light" | "dark",
    key: string,
    value: string
  ) => {
    try {
      const updated = { ...colors };
      updated[mode][key] = value;
      await saveColorsToDb(updated);
      setColors(updated);
      document.documentElement.style.setProperty(`--${key}`, value);
      toast.success(`Updated ${mode} mode ${key} color`);
    } catch {
      toast.error("Failed to update color");
      setError("Failed to update color");
    }
  };

  const resetColors = async () => {
    try {
      await saveColorsToDb(defaultColors);
      setColors(defaultColors);
      Object.entries(defaultColors.light).forEach(([k, v]) =>
        document.documentElement.style.setProperty(`--${k}`, v)
      );
      toast.success("Reset colors to default");
    } catch {
      toast.error("Failed to reset colors");
      setError("Failed to reset colors");
    }
  };

  const applyPreset = async (preset: ColorPreset) => {
    try {
      const newSet = {
        light: {
          ...colors.light,
          primary: preset.primary,
          accent: preset.accent,
        },
        dark: {
          ...colors.dark,
          primary: preset.primary,
          accent: preset.accent,
        },
      };
      await saveColorsToDb(newSet);
      setColors(newSet);
      document.documentElement.style.setProperty("--primary", preset.primary);
      document.documentElement.style.setProperty("--accent", preset.accent);
      toast.success(`Applied ${preset.name} preset`);
    } catch {
      toast.error("Failed to apply preset");
      setError("Failed to apply preset");
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        colors,
        updateColor,
        resetColors,
        applyPreset,
        saveColorsToDb,
        loading,
        error,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
